import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { getAuthToken } from "./config/apiConfig";
import { isModuleVisible } from "./utils/permissions";
import { ROUTES } from "./config/routes";
import Layout from "./components/Layout";
import Login from "./components/Login";
import OtpVerify from "./components/OtpVerify";
import Dashboard from "./components/Dashboard";
import Step1ProjectInfo from "./components/Step1ProjectInfo";
import Step2RiskInflation from "./components/Step2RiskInflation";
import Step3AssetSelection from "./components/Step3AssetSelection";
import Step4SubElement from "./components/Step4SubElement";
import Step5Final from "./components/Step5Final";
import EstimationSummary from "./components/EstimationSummary";
import BasisOfCost from "./components/BasisOfCost";
import ElementScopePage1 from "./components/ElementScopePage1";
// Read-only "view" counterparts of the 7 wizard steps -- separate
// components from the editable ones above, linked from
// EstimationSummary.jsx's step indicator so browsing a finished
// project's steps can never open (or accidentally touch) the real
// create/edit wizard flow.
import Step1ProjectInfoReadOnly from "./components/Step1ProjectInfoReadOnly";
import Step2RiskInflationReadOnly from "./components/Step2RiskInflationReadOnly";
import Step3AssetSelectionReadOnly from "./components/Step3AssetSelectionReadOnly";
import Step4SubElementReadOnly from "./components/Step4SubElementReadOnly";
import Step5FinalReadOnly from "./components/Step5FinalReadOnly";
import BasisOfCostReadOnly from "./components/BasisOfCostReadOnly";
import ElementScopePage1ReadOnly from "./components/ElementScopePage1ReadOnly";
import ProjectList from "./components/ProjectList";
import RateCard from "./components/RateCard";
import UsersList from "./components/UsersList";
import UserCreateForm from "./components/UserCreateForm";
import EditUserForm from "./components/EditUserForm";
import EditAccessRequestForm from "./components/EditAccessRequestForm";
import RoleManagement from "./components/RoleManagement";
import ManageRoles from "./components/ManageRoles";
import RoleMatrix from "./components/RoleMatrix";
import DepartmentManagement from "./components/DepartmentManagement";
import CompanyManagement from "./components/CompanyManagement";
// NOTE: SetPassword.jsx was delivered earlier in the same batch as
// UserCreateForm/UsersList/RoleManagement -- save it into src/components/
// too (it isn't in your folder screenshot yet), otherwise this import
// will fail the build. Once it's there, this line is already correct.
import SetPassword from "./components/SetPassword";
import ForgotPassword from "./components/ForgotPassword";
import AccessRequestForm from "./components/AccessRequestForm";
import ProfilePage from "./components/ProfilePage";
import ChangePasswordPage from "./components/ChangePasswordPage";
import SignOut from "./components/SignOut";
import Help from "./components/Help";
import Notifications from "./components/Notifications";
import EmailConfiguration from "./components/EmailConfiguration";
import TemplateCreatePage from "./components/TemplateCreatePage";
import AIAssistant from "./components/AIAssistant";
import MasterComingSoon from "./components/MasterComingSoon";

// NEW: every page inside <Layout> below calls protected API routes
// (see @jwt_required in app.py -- every route now requires a JWT except
// login/OTP/password-reset/access-request/company-dropdown/profile-photo).
// Without this guard, someone could still navigate straight to
// /dashboard (etc.) with no token at all -- the page would just render
// empty/broken as every fetch/axios call 401s. This catches it up
// front: no token in localStorage ("authToken", set by OtpVerify.jsx on
// successful login) means straight back to the login page instead.
function ProtectedRoute({ children }) {
  if (!getAuthToken()) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Closes the same hole hiding a nav link only patches: SideMenu.jsx and
// Header.jsx's Explore menu already hide links to modules the current
// tier can't see (see src/utils/permissions.js), but a hidden link
// doesn't stop someone from just typing the URL. This is that second
// layer -- same permission table, applied at the route itself. Backend
// write/delete endpoints have their own matching require_tier() check
// in app.py, so this is "don't even show the page", not the only line
// of defense against actually doing something forbidden.
function RoleProtectedRoute({ module, children }) {
  if (!isModuleVisible(module)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return children;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/otp" element={<OtpVerify />} />

        {/* Public: reached only via the emailed invite link, no
            sidebar/login needed to view it */}
        <Route path="/set-password" element={<SetPassword />} />

        {/* Public: email-based OTP password reset, linked from the
            "Forgot Password" link on the login card */}
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Public: pre-login "Raise Access Request" form, linked from
            the "Don't have account to login?" line on the login card */}
        <Route path="/access-request" element={<AccessRequestForm />} />

        {/* Clears the cached user and redirects to login -- kept
            outside Layout so it doesn't flash the sidebar/header
            while it does that. */}
        <Route path="/signout" element={<SignOut />} />

        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />

          {/* SideMenu's "Master" nav item -- per request, clicking Master
              now opens straight to this "Coming Soon" page instead of
              toggling the (now-empty) Role/Department/Company submenu. */}
          <Route path={ROUTES.MASTER} element={<RoleProtectedRoute module="Master"><MasterComingSoon /></RoleProtectedRoute>} />

          {/* SideMenu's "Project" nav item already links here --
              "PROJECT - COST ESTIMATION LOG" table (view/edit/delete,
              search, pagination). Edit is disabled server- and
              client-side once a project's status = 'C' (completed). */}
          <Route path={ROUTES.PROJECT} element={<ProjectList />} />

          {/* SideMenu's "Rate Card" nav item already links here -- "Not
              visible" to View per the Role Matrix, so route-guarded
              same as SideMenu.jsx/Header.jsx already hide its link. */}
          <Route path={ROUTES.RATE_CARD} element={<RoleProtectedRoute module="RateCard"><RateCard /></RoleProtectedRoute>} />

          {/* The wizard itself (create/edit an estimation) is blocked for
              View -- their matrix cell is "View – All estimations", i.e.
              read access, satisfied by /cost-estimation-summary below
              (reached from Project's own View action), not by this
              multi-step create/edit flow. EstimationSummary is
              deliberately NOT wrapped -- it's the read-only page View
              needs, not part of what's being restricted here. */}
          <Route path={ROUTES.COST_ESTIMATION_STEP1} element={<RoleProtectedRoute module="CostEstimation"><Step1ProjectInfo /></RoleProtectedRoute>} />
          <Route path={ROUTES.COST_ESTIMATION_STEP2} element={<RoleProtectedRoute module="CostEstimation"><Step2RiskInflation /></RoleProtectedRoute>} />
          <Route path={ROUTES.COST_ESTIMATION_STEP3} element={<RoleProtectedRoute module="CostEstimation"><Step3AssetSelection /></RoleProtectedRoute>} />
          <Route path={ROUTES.COST_ESTIMATION_STEP4} element={<RoleProtectedRoute module="CostEstimation"><Step4SubElement /></RoleProtectedRoute>} />
          <Route path={ROUTES.COST_ESTIMATION_STEP5} element={<RoleProtectedRoute module="CostEstimation"><Step5Final /></RoleProtectedRoute>} />
          <Route path={ROUTES.COST_ESTIMATION_SUMMARY} element={<EstimationSummary />} />
          <Route path={ROUTES.BASIS_OF_COST} element={<RoleProtectedRoute module="CostEstimation"><BasisOfCost /></RoleProtectedRoute>} />
          <Route path={ROUTES.ELEMENT_SCOPE_PAGE1} element={<RoleProtectedRoute module="CostEstimation"><ElementScopePage1 /></RoleProtectedRoute>} />

          {/* Read-only counterparts of the 7 routes above, linked from
              EstimationSummary.jsx's step indicator. Deliberately NOT
              wrapped in RoleProtectedRoute module="CostEstimation" --
              same reasoning as EstimationSummary just above: these are
              the read-only pages View-tier (and everyone else) reaches
              from Summary, not part of the editable wizard flow being
              restricted there. */}
          <Route path={ROUTES.COST_ESTIMATION_STEP1_VIEW} element={<Step1ProjectInfoReadOnly />} />
          <Route path={ROUTES.COST_ESTIMATION_STEP2_VIEW} element={<Step2RiskInflationReadOnly />} />
          <Route path={ROUTES.COST_ESTIMATION_STEP3_VIEW} element={<Step3AssetSelectionReadOnly />} />
          <Route path={ROUTES.COST_ESTIMATION_STEP4_VIEW} element={<Step4SubElementReadOnly />} />
          <Route path={ROUTES.COST_ESTIMATION_STEP5_VIEW} element={<Step5FinalReadOnly />} />
          <Route path={ROUTES.BASIS_OF_COST_VIEW} element={<BasisOfCostReadOnly />} />
          <Route path={ROUTES.ELEMENT_SCOPE_PAGE1_VIEW} element={<ElementScopePage1ReadOnly />} />

          {/* Account -> User / Role / Department / Company (SideMenu's flyout links here).
              "Not visible" to View/Contributor per the matrix. */}
          <Route path={ROUTES.ACCOUNT_USERS} element={<RoleProtectedRoute module="AccountUsers"><UsersList /></RoleProtectedRoute>} />
          <Route path={ROUTES.ACCOUNT_USERS_CREATE} element={<RoleProtectedRoute module="AccountUsers"><UserCreateForm /></RoleProtectedRoute>} />
          <Route path={`${ROUTES.ACCOUNT_USERS_EDIT}/:id`} element={<RoleProtectedRoute module="AccountUsers"><EditUserForm /></RoleProtectedRoute>} />

          {/* Access Request tab's View/Edit page -- one page for both
              actions (see EditAccessRequestForm.jsx), opened read-only
              for View or already in edit mode for Edit. */}
          <Route path={`${ROUTES.ACCESS_REQUEST_EDIT}/:id`} element={<RoleProtectedRoute module="AccountUsers"><EditAccessRequestForm /></RoleProtectedRoute>} />
          <Route path={ROUTES.ACCOUNT_ROLES} element={<RoleProtectedRoute module="Master"><RoleManagement /></RoleProtectedRoute>} />
          <Route path={ROUTES.ACCOUNT_DEPARTMENTS} element={<RoleProtectedRoute module="Master"><DepartmentManagement /></RoleProtectedRoute>} />
          <Route path={ROUTES.ACCOUNT_COMPANIES} element={<RoleProtectedRoute module="Master"><CompanyManagement /></RoleProtectedRoute>} />

          {/* Account -> Manage Roles: per-user assignment of the 4
              permission tiers from the Role Matrix doc (View /
              Contributor / Admin / Super Admin). Separate from
              /account/roles above, which is still the older
              add/rename/delete-role-name screen under Master. */}
          <Route path={ROUTES.ACCOUNT_MANAGE_ROLES} element={<RoleProtectedRoute module="AccountUsers"><ManageRoles /></RoleProtectedRoute>} />

          {/* Account -> Role Matrix: read-only display of the same table
              Manage Roles assigns tiers from and permissions.js enforces --
              a reference, not an editor. */}
          <Route path={ROUTES.ACCOUNT_ROLE_MATRIX} element={<RoleProtectedRoute module="AccountUsers"><RoleMatrix /></RoleProtectedRoute>} />

          {/* Header's avatar menu -> Profile */}
          <Route path={ROUTES.PROFILE} element={<ProfilePage />} />

          {/* Header's avatar menu -> Change Password (logged-in user;
              reuses the OTP + new-password backend from Forgot
              Password since there's no separate endpoint yet) */}
          <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePasswordPage />} />

          {/* SideMenu's "Help" nav item and the Explore mega-menu's Help
              item have both pointed here since they were written -- this
              is the page finally existing on the other end. */}
          <Route path={ROUTES.HELP} element={<Help />} />

          {/* Header bell dropdown's "View all" link */}
          <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />

          {/* SideMenu's Configuration submenu -> Mail Configuration.
              "Not visible" to everyone but Super Admin per the matrix. */}
          <Route path={ROUTES.CONFIG_MAIL} element={<RoleProtectedRoute module="Configuration"><EmailConfiguration /></RoleProtectedRoute>} />

          {/* Mail Configuration's Template tab -> "Add Template" (Screen 1
              of the create/upload vs. display flow -- Screen 2 is the
              Template tab itself, above) */}
          <Route path={ROUTES.CONFIG_MAIL_TEMPLATE_NEW} element={<RoleProtectedRoute module="Configuration"><TemplateCreatePage /></RoleProtectedRoute>} />

          {/* SideMenu's "AI Assistant" nav item -- AI Cost Estimation MVP */}
          <Route path={ROUTES.AI_ASSISTANT} element={<AIAssistant />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;